import { useState, useRef, useEffect, forwardRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CircleNotch, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { useAuthStore } from "../../stores/auth";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { APP_NAME, LeverMark } from "../../lib/brand";

const EASE_OUT: [number, number, number, number] = [0.32, 0.72, 0, 1];
const EASE_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];
const STEP_TRANSITION = { duration: 0.4, ease: EASE_OUT };

const isEmailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

type Step = "initial" | "password" | "signup" | "forgot-password";


function GoogleIcon() {
  return (
    <svg className="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function FilmGrain() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-0 opacity-[0.03]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: "256px 256px",
        mixBlendMode: "overlay",
      }}
    />
  );
}

export function LoginPage() {
  useDocumentTitle("Sign in");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    signInWithEmail, signUp, signInWithOAuth, signInAsDev,
    resetPassword, loading, devMode, user,
    pendingEmailConfirmation, clearPendingConfirmation,
  } = useAuthStore();

  const showDevLogin = devMode || import.meta.env.VITE_ENABLE_DEV_LOGIN === "true";

  const [step, setStep] = useState<Step>("initial");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const sessionExpired = searchParams.get("expired") === "1";

  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user && !sessionExpired) {
      navigate("/", { replace: true });
    }
  }, [user, sessionExpired, navigate]);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (step === "password" || step === "forgot-password") passwordRef.current?.focus();
      if (step === "signup") nameRef.current?.focus();
      if (step === "initial") emailRef.current?.focus();
    }, 420);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    return () => { clearPendingConfirmation(); };
  }, [clearPendingConfirmation]);

  const handleEmailContinue = () => {
    setError(null);
    if (!email.trim() || !isEmailValid(email)) {
      setError("Enter a valid email address");
      return;
    }
    setStep("password");
  };

  const handleSignIn = async () => {
    setError(null);
    if (!password.trim()) { setError("Enter your password"); return; }
    try {
      await signInWithEmail(email, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    }
  };

  const handleSignUp = async () => {
    setError(null);
    if (!name.trim()) { setError("Enter your name"); return; }
    if (!email.trim() || !isEmailValid(email)) { setError("Enter a valid email"); return; }
    if (!password.trim() || password.length < 6) { setError("Password must be 6+ characters"); return; }
    try {
      await signUp(email, password, name);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign up failed");
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    if (!email.trim() || !isEmailValid(email)) {
      setError("Enter a valid email address");
      return;
    }
    try {
      await resetPassword(email);
      setInfo("Password reset link sent. Check your email.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not send reset email");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === "initial") handleEmailContinue();
    else if (step === "password") handleSignIn();
    else if (step === "forgot-password") handleForgotPassword();
    else handleSignUp();
  };

  const handleGoogle = () => signInWithOAuth("google");

  const handleDevLogin = async () => {
    await signInAsDev();
    navigate("/", { replace: true });
  };

  const goBack = () => {
    setStep("initial");
    setPassword("");
    setError(null);
    setInfo(null);
  };

  if (pendingEmailConfirmation) {
    return (
      <div className="min-h-dvh bg-black flex flex-col items-center justify-center relative overflow-x-hidden overflow-y-auto">
        <FilmGrain />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE_EXPO }}
          className="relative z-[1] w-full max-w-[360px] px-6 text-center"
        >
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">
              <EnvelopeSimple size={24} weight="light" className="text-white/60" />
            </div>
          </div>
          <h1
            className="text-white mb-3"
            style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "22px", letterSpacing: "-0.03em" }}
          >
            Check your email
          </h1>
          <p className="text-white/40 text-[13px] mb-8" style={{ fontFamily: "var(--font-sans)" }}>
            We sent a confirmation link to{" "}
            <span className="text-white/60">{pendingEmailConfirmation}</span>.
            Click the link to activate your account.
          </p>
          <button
            type="button"
            onClick={() => { clearPendingConfirmation(); setStep("initial"); }}
            className="text-white/40 hover:text-white/60 transition-colors duration-200 underline underline-offset-2 decoration-white/20"
            style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            Back to sign in
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-black flex flex-col items-center justify-center relative overflow-x-hidden overflow-y-auto">
      <FilmGrain />

      {/* Back to landing */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={mounted ? { opacity: 1 } : undefined}
        transition={{ duration: 0.6, delay: 0.1 }}
        onClick={() => navigate("/landing")}
        className="fixed top-5 left-6 sm:left-10 flex items-center gap-2 text-white/30 hover:text-white/70 transition-colors duration-300 z-10"
        style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase" }}
      >
        <ArrowLeft size={12} />
        <span>Back</span>
      </motion.button>

      {/* Form container */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={mounted ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.8, delay: 0.15, ease: EASE_EXPO }}
        className="relative z-[1] w-full max-w-[360px] px-6"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={mounted ? { opacity: 1, scale: 1 } : undefined}
            transition={{ duration: 0.6, delay: 0.2, ease: EASE_EXPO }}
            className="text-white/60 mb-5"
          >
            <LeverMark size={24} />
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.h1
              key={step === "signup" ? "signup-title" : "signin-title"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: EASE_OUT }}
              className="text-white text-center select-none"
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600,
                fontSize: "22px",
                letterSpacing: "-0.03em",
              }}
            >
              {step === "signup" ? "Create your account" : `Sign in to ${APP_NAME}`}
            </motion.h1>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p
              key={step === "signup" ? "signup-sub" : "signin-sub"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, delay: 0.05 }}
              className="text-white/40 text-center mt-2"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {step === "signup"
                ? `Start writing with ${APP_NAME}`
                : "Technical editor & writing tool"}
            </motion.p>
          </AnimatePresence>
        </div>

        {sessionExpired && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 px-4 py-3 border border-amber-500/20 bg-amber-500/[0.06] text-center"
          >
            <p className="text-amber-400/80 text-[12px]" style={{ fontFamily: "var(--font-sans)" }}>
              Your session expired. Please sign in again.
            </p>
          </motion.div>
        )}

        {!devMode && (
          <>
            <div className="space-y-3">
              <motion.button
                type="button"
                onClick={handleGoogle}
                disabled={loading}
                whileHover={{ scale: 1.005 }}
                whileTap={{ scale: 0.995 }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3
                  bg-white text-[#1f1f1f] text-[13px] font-medium
                  hover:bg-white/95 active:bg-white/90
                  transition-colors duration-150
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleIcon />
                Continue with Google
              </motion.button>
            </div>

            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-white/8" />
              <span
                className="text-white/30"
                style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                or
              </span>
              <div className="flex-1 h-px bg-white/8" />
            </div>
          </>
        )}

        {showDevLogin && (
          <>
            <motion.button
              type="button"
              onClick={handleDevLogin}
              whileHover={{ scale: 1.005 }}
              whileTap={{ scale: 0.995 }}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 mb-3
                bg-white/[0.06] border border-dashed border-white/[0.12] text-white/50 text-[13px]
                hover:bg-white/[0.1] hover:text-white/70 hover:border-white/20
                transition-colors duration-150"
            >
              Continue as dev user
            </motion.button>

            {devMode && (
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-white/8" />
                <span
                  className="text-white/30"
                  style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  dev mode
                </span>
                <div className="flex-1 h-px bg-white/8" />
              </div>
            )}
          </>
        )}

        {/* Email form with progressive disclosure */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-0">
            {/* Email field — visible on initial + password steps */}
            {step !== "signup" && (
              <div className="mb-3">
                <FloatingField
                  ref={emailRef}
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={(v) => { setEmail(v); setError(null); }}
                  disabled={step === "password"}
                  autoFocus={step === "initial"}
                />
                {step === "password" && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="flex items-center gap-1.5 mt-2 text-white/40 hover:text-white/60 transition-colors duration-200"
                    style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.04em" }}
                  >
                    <ArrowLeft size={10} />
                    Change
                  </button>
                )}
              </div>
            )}

            <AnimatePresence mode="wait" initial={false}>
              {step === "initial" && (
                <motion.div
                  key="step-initial"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={STEP_TRANSITION}
                  style={{ overflow: "hidden" }}
                >
                  <div className="pt-1 pb-2">
                    <SubmitButton loading={loading}>Continue</SubmitButton>
                  </div>
                </motion.div>
              )}

              {step === "password" && (
                <motion.div
                  key="step-password"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={STEP_TRANSITION}
                  style={{ overflow: "hidden" }}
                >
                  <div className="pt-1 space-y-3 pb-2">
                    <FloatingField
                      ref={passwordRef}
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(v) => { setPassword(v); setError(null); }}
                    />
                    <SubmitButton loading={loading}>Sign in</SubmitButton>
                    {!devMode && (
                      <div className="text-center pt-1">
                        <button
                          type="button"
                          onClick={() => { setStep("forgot-password"); setError(null); setInfo(null); setPassword(""); }}
                          className="text-white/35 hover:text-white/55 transition-colors duration-200"
                          style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.04em" }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {step === "forgot-password" && (
                <motion.div
                  key="step-forgot"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={STEP_TRANSITION}
                  style={{ overflow: "hidden" }}
                >
                  <div className="pt-1 space-y-3 pb-2">
                    <p className="text-white/40 text-[12px] text-center" style={{ fontFamily: "var(--font-sans)" }}>
                      We&apos;ll send a reset link to <span className="text-white/60">{email}</span>
                    </p>
                    <SubmitButton loading={loading}>Send reset link</SubmitButton>
                  </div>
                </motion.div>
              )}

              {step === "signup" && (
                <motion.div
                  key="step-signup"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={STEP_TRANSITION}
                  style={{ overflow: "hidden" }}
                >
                  <div className="space-y-3 pb-2">
                    {([
                      { key: "name", el: (
                        <FloatingField
                          ref={nameRef}
                          type="text"
                          label="Full name"
                          value={name}
                          onChange={(v) => { setName(v); setError(null); }}
                        />
                      )},
                      { key: "email", el: (
                        <FloatingField
                          type="email"
                          label="Email address"
                          value={email}
                          onChange={(v) => { setEmail(v); setError(null); }}
                        />
                      )},
                      { key: "password", el: (
                        <FloatingField
                          type="password"
                          label="Password"
                          value={password}
                          onChange={(v) => { setPassword(v); setError(null); }}
                        />
                      )},
                    ] as const).map((field, i) => (
                      <motion.div
                        key={field.key}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: i * 0.08, ease: EASE_OUT }}
                      >
                        {field.el}
                      </motion.div>
                    ))}
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: 0.24, ease: EASE_OUT }}
                    >
                      <SubmitButton loading={loading}>Create account</SubmitButton>
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {error && (
                <motion.p
                  key="error"
                  initial={{ opacity: 0, y: -4, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -4, height: 0 }}
                  transition={{ duration: 0.25, ease: EASE_OUT }}
                  className="text-[#ef4444] text-center overflow-hidden"
                  style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  <span className="block pt-3">{error}</span>
                </motion.p>
              )}
              {info && !error && (
                <motion.div
                  key="info"
                  initial={{ opacity: 0, y: -4, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -4, height: 0 }}
                  transition={{ duration: 0.25, ease: EASE_OUT }}
                  className="text-center overflow-hidden"
                >
                  <span className="flex items-center justify-center gap-2 pt-3 text-emerald-400/80" style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.02em" }}>
                    <CheckCircle size={14} weight="fill" />
                    {info}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>

        <div className="mt-8 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={step === "signup" ? "has-account" : step === "forgot-password" ? "back-signin" : "no-account"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="text-white/35"
              style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              {step === "signup"
                ? "Already have an account? "
                : step === "forgot-password"
                ? "Remember your password? "
                : "Don't have an account? "}
              <button
                type="button"
                onClick={() => {
                  setStep(step === "signup" ? "initial" : step === "forgot-password" ? "password" : "signup");
                  setError(null);
                  setInfo(null);
                  setPassword("");
                }}
                className="text-white/60 hover:text-white/80 transition-colors duration-200 underline underline-offset-2 decoration-white/30 hover:decoration-white/50"
              >
                {step === "signup" ? "Sign in" : step === "forgot-password" ? "Sign in" : "Sign up"}
              </button>
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>

      <p
        className="fixed bottom-6 text-white/15"
        style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase" }}
      >
        {APP_NAME}
      </p>
    </div>
  );
}

/* ─── Floating-label input: label moves to top-left on focus/value ─── */

const FloatingField = forwardRef<
  HTMLInputElement,
  {
    type?: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    autoFocus?: boolean;
  }
>(function FloatingField({ type = "text", label, value, onChange, disabled, autoFocus }, ref) {
  const [focused, setFocused] = useState(false);
  const active = focused || value.length > 0;

  return (
    <div className="relative">
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete={type === "password" ? "current-password" : type === "email" ? "email" : "name"}
        className="peer w-full px-4 pt-5 pb-2 text-[13px] text-white
          bg-white/[0.04] border border-white/[0.08]
          focus:border-white/20 focus:bg-white/[0.06]
          disabled:text-white/30 disabled:cursor-default
          outline-none transition-all duration-200"
        style={{ fontFamily: "var(--font-sans)" }}
      />
      <label
        className="absolute left-4 pointer-events-none transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          fontFamily: "var(--font-sans)",
          top: active ? "6px" : "14px",
          fontSize: active ? "9px" : "13px",
          letterSpacing: active ? "0.06em" : "0",
          textTransform: active ? "uppercase" : "none",
          color: active
            ? focused ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.35)"
            : "rgba(255,255,255,0.35)",
        }}
      >
        {label}
      </label>
    </div>
  );
});

/* ─── Submit button ─── */

function SubmitButton({ loading, children }: { loading: boolean; children: React.ReactNode }) {
  return (
    <motion.button
      type="submit"
      disabled={loading}
      whileHover={{ scale: 1.005 }}
      whileTap={{ scale: 0.995 }}
      className="w-full flex items-center justify-center gap-2 px-4 py-3
        bg-white/[0.08] border border-white/[0.08] text-white/80 text-[13px] font-medium
        hover:bg-white/[0.12] hover:text-white hover:border-white/[0.12]
        active:bg-white/[0.06]
        transition-all duration-150
        disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ fontFamily: "var(--font-sans)" }}
    >
      {loading ? (
        <CircleNotch size={16} weight="bold" className="animate-spin text-white/60" />
      ) : (
        children
      )}
    </motion.button>
  );
}
